/**
 * "Create Solution" — shown under the CRM solution on the New Resource Plan form when the
 * picked NOS Deal has no Solution in NOS (spec R3/R4, D1).
 *
 * A name is all it asks for. It starts as the Deal's name, cut to NOS's 50-character
 * limit. Saving creates the Solution in NOS on the Deal's Account and links it to the
 * Deal; everything else on it starts blank. The form owns the request — this only
 * collects the name — and mounts it with `key={dealId}` so each Deal starts fresh.
 */

import { useState } from "react";
import type { PlanDealOption } from "@/api/plan-deal-options";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DEAL_SOLUTION_NAME_MAX } from "@/schemas/plan-deal-solution";

interface Props {
  deal: PlanDealOption;
  pending: boolean;
  onCreate: (name: string) => void;
}

export function DealSolutionCreate({ deal, pending, onCreate }: Props) {
  const [name, setName] = useState(() => deal.dealName.trim().slice(0, DEAL_SOLUTION_NAME_MAX));
  const trimmed = name.trim();
  const submit = () => {
    if (trimmed !== "" && !pending) onCreate(trimmed);
  };

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      <p className="text-sm">This Deal has no Solution in NOS yet. Name one to plan against.</p>
      <div className="flex gap-2">
        <Input
          aria-label="New Solution name"
          value={name}
          maxLength={DEAL_SOLUTION_NAME_MAX}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            // Enter here creates the Solution; it must not submit the plan form around it.
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          disabled={pending}
        />
        <Button type="button" onClick={submit} disabled={pending || trimmed === ""}>
          {pending ? "Creating…" : "Create Solution"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Creates it in NOS on {deal.accountName} and links it to this Deal. Add its details in NOS
        later.
      </p>
    </div>
  );
}
